import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { buildLoginPath, consumeExplicitLogout } from "@/lib/loginRedirect";
import { decimalInputProps, decimalOnly, integerInputProps, integerOnly } from "@/lib/numberInput";
import { ORDER_STATUS_OPTIONS, getOrderStatusClass, getOrderStatusLabel, normalizeOrderStatus } from "@/lib/orderStatus";
import { toast } from "sonner";

function isSteelTmtItem(item) {
  const slug = String(item?.product_slug || "").toLowerCase();
  const name = String(item?.product_name || "").toLowerCase();
  return slug === "steel-tmt" || name.includes("steel tmt");
}

function formatQuantityNumber(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value || "N/A";
  return numericValue.toLocaleString("en-IN", { maximumFractionDigits: 3 });
}

function formatCurrency(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

function getQuantityUnitLabel(item) {
  const unit = String(item?.unit || "").trim();
  const normalizedUnit = unit.toLowerCase();

  if (isSteelTmtItem(item)) return "tonnes";
  if (normalizedUnit === "mt") return "tonnes";
  if (normalizedUnit === "bag") return "bags";
  if (normalizedUnit === "piece") return "pieces";
  return unit;
}

function formatOrderItemQuantity(item) {
  const unitLabel = getQuantityUnitLabel(item);
  return [formatQuantityNumber(item?.quantity), unitLabel].filter(Boolean).join(" ");
}

const SENT_STATUS_OPTIONS = [
  { value: "not_sent", label: "Not Sent" },
  { value: "sent", label: "Sent" },
];

const SUBSCRIBER_STATUS_OPTIONS = [
  { value: "not_added", label: "Not Added" },
  { value: "added", label: "Added" },
];

const DASHBOARD_REFRESH_INTERVAL_MS = 30000;

function normalizeSentStatus(value) {
  const status = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (status === "new") return "not_sent";
  return SENT_STATUS_OPTIONS.some((option) => option.value === status) ? status : "not_sent";
}

function normalizeSubscriberStatus(value) {
  const status = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return SUBSCRIBER_STATUS_OPTIONS.some((option) => option.value === status) ? status : "not_added";
}

function getOptionLabel(options, value) {
  return options.find((option) => option.value === value)?.label || value;
}

function AdminTableScroll({ children }) {
  return (
    <div className="max-h-[292px] overflow-auto">
      {children}
    </div>
  );
}

function AdminCardScroll({ children }) {
  return (
    <div className="max-h-[640px] overflow-y-auto p-4 md:p-6">
      {children}
    </div>
  );
}

function StatusSelect({ value, options, onChange, disabled, ariaLabel }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      aria-label={ariaLabel}
      className="w-32 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs font-semibold text-[#111827] focus:border-[#C8A96A] focus:outline-none focus:ring-2 focus:ring-[#C8A96A]/20 disabled:opacity-60"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
}

function CartAdditionContractorCard({ group }) {
  const visibleItems = group.items.slice(0, 4);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-sm font-semibold text-[#111827]">{group.contractor_company}</div>
          <div className="mt-1 text-xs text-[#4B5563]">
            {group.contractor_name} · {group.contractor_phone}
          </div>
          {group.contractor_email && (
            <div className="mt-0.5 text-xs text-[#4B5563]">{group.contractor_email}</div>
          )}
        </div>
        <div className="text-left sm:text-right">
          <div className="text-xs uppercase tracking-wider text-[#4B5563]">Cart Value</div>
          <div className="text-sm font-semibold text-[#111827]">{formatCurrency(group.total)}</div>
          <div className="mt-1 text-xs text-[#4B5563]">Last added: {formatDateTime(group.latest_at)}</div>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-md border border-gray-100">
        <div className="min-w-[820px]">
          <div className="grid grid-cols-[1.4fr_0.9fr_0.8fr_1fr_1fr] bg-gray-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-[#4B5563]">
            <div>Product</div>
            <div>Size/Brand</div>
            <div>Quantity</div>
            <div>Vendor</div>
            <div>Added At</div>
          </div>
          <div className="divide-y divide-gray-100">
            {visibleItems.map((item, index) => (
              <div key={item.id || `${group.key}-${index}`} className="grid min-h-14 grid-cols-[1.4fr_0.9fr_0.8fr_1fr_1fr] gap-2 px-3 py-2 text-xs text-[#111827]">
                <div className="font-semibold">{item.product_name || "-"}</div>
                <div className="text-[#4B5563]">{[item.size, item.brand].filter(Boolean).join(" / ") || "-"}</div>
                <div>{[formatQuantityNumber(item.quantity), item.unit].filter(Boolean).join(" ")}</div>
                <div className="text-[#4B5563]">{item.vendor_company || item.vendor_name || "-"}</div>
                <div className="text-[#4B5563]">{formatDateTime(item.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {group.items.length > visibleItems.length && (
        <div className="mt-2 text-xs text-[#4B5563]">
          +{group.items.length - visibleItems.length} more cart item{group.items.length - visibleItems.length === 1 ? "" : "s"} in the detailed table.
        </div>
      )}
    </div>
  );
}

function OrderItemsColumn({ items, getValue, className = "" }) {
  if (!items?.length) {
    return <div className="text-xs text-[#4B5563]">N/A</div>;
  }

  return (
    <div className={`space-y-1 ${className}`}>
      {items.map((item, idx) => (
        <div
          key={`${item?.id || item?.size || "item"}-${idx}`}
          className={`min-h-6 text-xs text-[#111827] whitespace-nowrap ${idx > 0 ? "border-t border-gray-100 pt-1" : ""}`}
        >
          {getValue(item)}
        </div>
      ))}
    </div>
  );
}

function getOrderItemVendor(item) {
  return item?.vendor_company || item?.vendor_name || "N/A";
}

function VendorProductsTable({ vendor }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingRowId, setSavingRowId] = useState("");
  const vendorId = vendor?.id;

  const loadRows = async () => {
    if (!vendorId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/vendors/${vendorId}/products`);
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!vendorId) return;
    loadRows();
  }, [vendorId]);

  if (!vendorId) return null;

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const saveRow = async (row) => {
    setSavingRowId(row.id);
    try {
      await api.patch(`/admin/vendor-products/${row.id}`, {
        size: row.size,
        brand: row.brand,
        location: row.location,
        price: Number(row.price),
        unit: row.unit,
        quantity: Number(row.quantity),
      });
      toast.success("Vendor product updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingRowId("");
    }
  };

  return (
    <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-gray-50 text-xs text-[#4B5563]">
        Vendor Products ({rows.length})
      </div>
      {loading ? (
        <div className="p-4 text-sm text-[#4B5563]">Loading products...</div>
      ) : rows.length === 0 ? (
        <div className="p-4 text-sm text-[#4B5563]">No products added yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-max w-full text-sm">
            <thead className="bg-white text-[#111827]">
              <tr>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider">Product</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider">Size</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider">Brand</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider">Location</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider">Price</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider">Qty</th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-gray-100">
                  <td className="py-2 px-3">{row.product_slug}</td>
                  <td className="py-2 px-3">
                    <input
                      value={row.size}
                      onChange={(e) => updateRow(row.id, { size: e.target.value })}
                      className="w-32 bg-white border border-gray-200 rounded-sm px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <input
                      value={row.brand}
                      onChange={(e) => updateRow(row.id, { brand: e.target.value })}
                      className="w-28 bg-white border border-gray-200 rounded-sm px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <input
                      value={row.location}
                      onChange={(e) => updateRow(row.id, { location: e.target.value })}
                      className="w-28 bg-white border border-gray-200 rounded-sm px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      <input
                        {...decimalInputProps}
                        value={row.price}
                        onChange={(e) => updateRow(row.id, { price: decimalOnly(e.target.value, 2) })}
                        className="w-24 bg-white border border-gray-200 rounded-sm px-2 py-1 text-xs"
                      />
                      <input
                        value={row.unit}
                        onChange={(e) => updateRow(row.id, { unit: e.target.value })}
                        className="w-12 bg-white border border-gray-200 rounded-sm px-2 py-1 text-xs"
                      />
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <input
                      {...integerInputProps}
                      value={row.quantity}
                      onChange={(e) => updateRow(row.id, { quantity: integerOnly(e.target.value) })}
                      className="w-20 bg-white border border-gray-200 rounded-sm px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button
                      onClick={() => saveRow(row)}
                      disabled={savingRowId === row.id}
                      className="btn-gold rounded-sm px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                    >
                      Save
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [cartAdditions, setCartAdditions] = useState([]);
  const [expandedVendorId, setExpandedVendorId] = useState("");
  const [busyAdminId, setBusyAdminId] = useState("");
  const [busyVendorId, setBusyVendorId] = useState("");
  const [updatingGstOrderId, setUpdatingGstOrderId] = useState("");
  const [updatingOrderStatusId, setUpdatingOrderStatusId] = useState("");
  const [updatingQuoteStatusId, setUpdatingQuoteStatusId] = useState("");
  const [updatingContactStatusId, setUpdatingContactStatusId] = useState("");
  const [updatingSubscriberStatusId, setUpdatingSubscriberStatusId] = useState("");
  const [orderSearch, setOrderSearch] = useState("");

  const pendingVendors = useMemo(
    () => vendors.filter((vendor) => !vendor.vendor_approved),
    [vendors]
  );
  const cartAdditionsByContractor = useMemo(() => {
    const groups = new Map();

    cartAdditions.forEach((item) => {
      const key = item.user_id || item.contractor_email || item.contractor_phone || item.contractor_company || "unknown";
      const existing = groups.get(key) || {
        key,
        contractor_company: item.contractor_company || "Contractor Company",
        contractor_name: item.contractor_name || "Contractor",
        contractor_phone: item.contractor_phone || "-",
        contractor_email: item.contractor_email || "",
        latest_at: item.created_at || "",
        items: [],
      };

      existing.items.push(item);
      if (!existing.latest_at || new Date(item.created_at || 0) > new Date(existing.latest_at || 0)) {
        existing.latest_at = item.created_at || "";
      }
      groups.set(key, existing);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        total: group.items.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0),
      }))
      .sort((a, b) => new Date(b.latest_at || 0) - new Date(a.latest_at || 0));
  }, [cartAdditions]);
  const filteredOrders = useMemo(() => {
    const query = orderSearch.trim().toLowerCase();
    if (!query) return orders;
    return orders.filter((order) => {
      const orderId = String(order.id || "").toLowerCase();
      const contractorName = String(order.contractor_name || "").toLowerCase();
      const contractorCompany = String(order.contractor_company || "").toLowerCase();
      return orderId.includes(query) || contractorName.includes(query) || contractorCompany.includes(query);
    });
  }, [orders, orderSearch]);

  const loadDashboard = useCallback(async ({ silent = false } = {}) => {
    try {
      const [
        { data: adminsData },
        { data: vendorsData },
        { data: ordersData },
        { data: quotesData },
        { data: contactsData },
        { data: subscribersData },
        { data: cartAdditionsData },
      ] = await Promise.all([
        api.get("/admin/admins"),
        api.get("/admin/vendors"),
        api.get("/admin/orders"),
        api.get("/admin/quotes"),
        api.get("/admin/contacts"),
        api.get("/admin/subscribers"),
        api.get("/admin/cart-additions"),
      ]);
      setAdmins(adminsData || []);
      setVendors(vendorsData || []);
      setOrders(ordersData || []);
      setQuotes((quotesData || []).map((quote) => ({ ...quote, status: normalizeSentStatus(quote.status) })));
      setContacts((contactsData || []).map((contact) => ({ ...contact, status: normalizeSentStatus(contact.status) })));
      setSubscribers((subscribersData || []).map((subscriber) => ({
        ...subscriber,
        status: normalizeSubscriberStatus(subscriber.status),
      })));
      setCartAdditions(cartAdditionsData || []);
    } catch (err) {
      if (!silent) toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      if (consumeExplicitLogout()) {
        navigate("/", { replace: true });
        return;
      }
      navigate(buildLoginPath("/admin-dashboard"), { replace: true });
      return;
    }
    if (!loading && user && user.role !== "admin") {
      navigate("/");
      return;
    }
    if (user?.role === "admin") {
      loadDashboard();
    }
  }, [user, loading, navigate, loadDashboard]);

  useEffect(() => {
    if (loading || user?.role !== "admin") return undefined;

    const refreshDashboard = () => {
      if (document.visibilityState === "visible") {
        loadDashboard({ silent: true });
      }
    };

    const intervalId = window.setInterval(refreshDashboard, DASHBOARD_REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", refreshDashboard);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshDashboard);
    };
  }, [loading, user?.role, loadDashboard]);

  const updateAdminApproval = async (adminId, approve) => {
    setBusyAdminId(adminId);
    try {
      await api.post(`/admin/admins/${adminId}/approval`, { approve });
      toast.success(approve ? "Admin approved" : "Admin moved to pending");
      await loadDashboard();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyAdminId("");
    }
  };

  const updateVendorApproval = async (vendorId, approve) => {
    setBusyVendorId(vendorId);
    try {
      await api.post(`/admin/vendors/${vendorId}/approval`, { approve });
      toast.success(approve ? "Vendor approved" : "Vendor moved to pending");
      await loadDashboard();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyVendorId("");
    }
  };

  const updateGstStatus = async (orderId, done) => {
    setUpdatingGstOrderId(orderId);
    try {
      await api.patch(`/admin/orders/${orderId}/gst-status`, { done });
      toast.success(`GST status updated to ${done ? "Done" : "Not Done"}`);
      setOrders((prev) => prev.map((row) => (
        row.id === orderId ? { ...row, gst_input_done: done } : row
      )));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdatingGstOrderId("");
    }
  };

  const updateOrderStatus = async (orderId, orderStatus) => {
    const nextStatus = normalizeOrderStatus(orderStatus);
    setUpdatingOrderStatusId(orderId);
    try {
      await api.patch(`/admin/orders/${orderId}/order-status`, { order_status: nextStatus });
      toast.success(`Order status updated to ${getOrderStatusLabel(nextStatus)}`);
      setOrders((prev) => prev.map((row) => (
        row.id === orderId ? { ...row, order_status: nextStatus } : row
      )));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdatingOrderStatusId("");
    }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    const nextStatus = normalizeSentStatus(status);
    setUpdatingQuoteStatusId(quoteId);
    try {
      await api.patch(`/admin/quotes/${quoteId}/status`, { status: nextStatus });
      toast.success(`Quote status updated to ${getOptionLabel(SENT_STATUS_OPTIONS, nextStatus)}`);
      setQuotes((prev) => prev.map((row) => (
        row.id === quoteId ? { ...row, status: nextStatus } : row
      )));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdatingQuoteStatusId("");
    }
  };

  const updateContactStatus = async (contactId, status) => {
    const nextStatus = normalizeSentStatus(status);
    setUpdatingContactStatusId(contactId);
    try {
      await api.patch(`/admin/contacts/${contactId}/status`, { status: nextStatus });
      toast.success(`Contact status updated to ${getOptionLabel(SENT_STATUS_OPTIONS, nextStatus)}`);
      setContacts((prev) => prev.map((row) => (
        row.id === contactId ? { ...row, status: nextStatus } : row
      )));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdatingContactStatusId("");
    }
  };

  const updateSubscriberStatus = async (subscriberId, status) => {
    const nextStatus = normalizeSubscriberStatus(status);
    setUpdatingSubscriberStatusId(subscriberId);
    try {
      await api.patch(`/admin/subscribers/${subscriberId}/status`, { status: nextStatus });
      toast.success(`Subscriber status updated to ${getOptionLabel(SUBSCRIBER_STATUS_OPTIONS, nextStatus)}`);
      setSubscribers((prev) => prev.map((row) => (
        row.id === subscriberId ? { ...row, status: nextStatus } : row
      )));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdatingSubscriberStatusId("");
    }
  };

  const deleteVendor = async (vendor) => {
    const firstConfirm = window.confirm(`Delete vendor "${vendor.company}"? This action cannot be undone.`);
    if (!firstConfirm) return;
    const secondConfirm = window.confirm("Please confirm again: permanently delete this vendor and all vendor products?");
    if (!secondConfirm) return;

    setBusyVendorId(vendor.id);
    try {
      await api.delete(`/admin/vendors/${vendor.id}`);
      toast.success("Vendor deleted");
      if (expandedVendorId === vendor.id) setExpandedVendorId("");
      await loadDashboard();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyVendorId("");
    }
  };

  if (loading || !user || user.role !== "admin") {
    return (
      <div className="bg-white min-h-screen">
        <Navbar />
        <div className="py-32 text-center text-[#4B5563]">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16 flex flex-col">
        <div className="mb-8">
          <div className="section-label">Admin</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#111827] mt-2 tracking-tight">
            Vendor & Contractor Dashboard
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-7 gap-4 mb-8">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-[#4B5563]">Total Vendors</div>
            <div className="text-2xl font-bold text-[#111827] mt-1">{vendors.length}</div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-[#4B5563]">Pending Approvals</div>
            <div className="text-2xl font-bold text-[#111827] mt-1">{pendingVendors.length}</div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-[#4B5563]">Contractor Orders</div>
            <div className="text-2xl font-bold text-[#111827] mt-1">{orders.length}</div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-[#4B5563]">Added to Cart</div>
            <div className="text-2xl font-bold text-[#111827] mt-1">{cartAdditions.length}</div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-[#4B5563]">Quote Requested</div>
            <div className="text-2xl font-bold text-[#111827] mt-1">{quotes.length}</div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-[#4B5563]">Contact Information</div>
            <div className="text-2xl font-bold text-[#111827] mt-1">{contacts.length}</div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-[#4B5563]">Subscribers</div>
            <div className="text-2xl font-bold text-[#111827] mt-1">{subscribers.length}</div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-8" style={{ order: 50 }}>
          <div className="px-4 md:px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-[#111827]">Added to Cart by Contractor</h2>
          </div>
          <AdminCardScroll>
            {cartAdditionsByContractor.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-sm text-[#4B5563]">
                No contractor cart additions available.
              </div>
            ) : (
              <div className="space-y-4">
                {cartAdditionsByContractor.map((group) => (
                  <CartAdditionContractorCard key={group.key} group={group} />
                ))}
              </div>
            )}
          </AdminCardScroll>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-8" style={{ order: 60 }}>
          <div className="px-4 md:px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-[#111827]">Vendor Approval Queue</h2>
          </div>
          <div className="px-4 md:px-6 py-4 border-b border-gray-100">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[#111827]">Admin Approval Queue</h3>
                <p className="mt-1 text-xs text-[#4B5563]">Only approved admin accounts can log in to the admin portal.</p>
              </div>
              <div className="text-xs font-semibold text-[#4B5563]">
                {admins.filter((admin) => !admin.vendor_approved).length} pending
              </div>
            </div>
            <div className="mt-3 overflow-hidden border border-gray-200">
              <AdminTableScroll>
                <table className="min-w-max w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-gray-50 text-[#111827]">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Admin</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Email</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Created</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Status</th>
                      <th className="text-right py-3 px-4 text-xs uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-6 px-4 text-[#4B5563]">No admin accounts available.</td>
                      </tr>
                    ) : (
                      admins.map((admin) => (
                        <tr key={admin.id} className="border-t border-gray-100">
                          <td className="py-3 px-4">
                            <div className="font-semibold text-[#111827]">{admin.name || "Admin"}</div>
                            <div className="text-xs text-[#4B5563]">{admin.company || "RailAxis"}</div>
                          </td>
                          <td className="py-3 px-4 text-[#111827]">{admin.email || "-"}</td>
                          <td className="py-3 px-4 text-xs text-[#4B5563] whitespace-nowrap">
                            {admin.created_at ? new Date(admin.created_at).toLocaleString() : "-"}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`text-xs px-2 py-1 rounded-full ${admin.vendor_approved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                              {admin.vendor_approved ? "Approved" : "Pending"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="inline-flex gap-2">
                              <button
                                onClick={() => updateAdminApproval(admin.id, true)}
                                disabled={busyAdminId === admin.id || admin.vendor_approved}
                                className="btn-gold rounded-sm px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => updateAdminApproval(admin.id, false)}
                                disabled={busyAdminId === admin.id || !admin.vendor_approved || admin.is_current_admin}
                                className="bg-white border border-gray-300 text-[#111827] rounded-sm px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                              >
                                Mark Pending
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </AdminTableScroll>
            </div>
          </div>
          <div className="px-4 md:px-6 py-3 bg-gray-50 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-[#111827]">Vendor Approval Queue</h3>
          </div>
          <AdminTableScroll>
            <table className="min-w-max w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 text-[#111827]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Vendor</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Phone</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Vendor ID</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Products Added</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Status</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((vendor) => (
                  <tr key={vendor.id} className="border-t border-gray-100">
                    <td className="py-3 px-4">
                      <div className="font-semibold text-[#111827]">{vendor.company}</div>
                      <div className="text-xs text-[#4B5563]">{vendor.name}</div>
                    </td>
                    <td className="py-3 px-4">{vendor.phone || "-"}</td>
                    <td className="py-3 px-4 text-xs">{vendor.vendor_code || "-"}</td>
                    <td className="py-3 px-4">{vendor.product_count}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${vendor.vendor_approved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                        {vendor.vendor_approved ? "Approved" : "Pending"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => updateVendorApproval(vendor.id, true)}
                          disabled={busyVendorId === vendor.id || vendor.vendor_approved}
                          className="btn-gold rounded-sm px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => updateVendorApproval(vendor.id, false)}
                          disabled={busyVendorId === vendor.id || !vendor.vendor_approved}
                          className="bg-white border border-gray-300 text-[#111827] rounded-sm px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                        >
                          Mark Pending
                        </button>
                        <button
                          onClick={() => setExpandedVendorId((prev) => (prev === vendor.id ? "" : vendor.id))}
                          className="bg-white border border-gray-300 text-[#111827] rounded-sm px-3 py-1.5 text-xs font-semibold"
                        >
                          {expandedVendorId === vendor.id ? "Hide Products" : "View Products"}
                        </button>
                        <button
                          onClick={() => deleteVendor(vendor)}
                          disabled={busyVendorId === vendor.id}
                          className="bg-red-50 border border-red-200 text-red-700 rounded-sm px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                        >
                          Delete Vendor
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableScroll>
          <div className="px-4 md:px-6 py-3 border-t border-gray-100 text-xs text-[#4B5563]">
            Vendors are allowed OTP login only after approval.
          </div>
        </div>

        {expandedVendorId && (
          <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 mb-8" style={{ order: 61 }}>
            <h3 className="font-semibold text-[#111827]">Products by selected vendor</h3>
            <VendorProductsTable vendor={vendors.find((vendor) => vendor.id === expandedVendorId)} />
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-8" style={{ order: 20 }}>
          <div className="px-4 md:px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-[#111827]">Quote Requested</h2>
          </div>
          <AdminTableScroll>
            <table className="min-w-max w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 text-[#111827]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Date</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Product</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Quantity</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Company</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Mobile</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Email</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Pincode</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {quotes.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-6 px-4 text-[#4B5563]">No quote requests available.</td>
                  </tr>
                ) : (
                  quotes.map((quote) => (
                    <tr key={quote.id} className="border-t border-gray-100">
                      <td className="py-3 px-4 text-xs text-[#4B5563]">
                        {quote.created_at ? new Date(quote.created_at).toLocaleString() : "-"}
                      </td>
                      <td className="py-3 px-4 font-semibold text-[#111827]">{quote.product || "-"}</td>
                      <td className="py-3 px-4 text-[#111827]">{quote.quantity || "-"}</td>
                      <td className="py-3 px-4 text-[#111827]">{quote.company || "-"}</td>
                      <td className="py-3 px-4 text-[#111827]">{quote.mobile || "-"}</td>
                      <td className="py-3 px-4 text-[#4B5563]">{quote.email || "-"}</td>
                      <td className="py-3 px-4 text-[#4B5563]">{quote.pincode || "-"}</td>
                      <td className="py-3 px-4">
                        <StatusSelect
                          value={normalizeSentStatus(quote.status)}
                          options={SENT_STATUS_OPTIONS}
                          onChange={(status) => updateQuoteStatus(quote.id, status)}
                          disabled={updatingQuoteStatusId === quote.id}
                          ariaLabel="Update quote request status"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </AdminTableScroll>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-8" style={{ order: 30 }}>
          <div className="px-4 md:px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-[#111827]">Contact Information</h2>
          </div>
          <AdminTableScroll>
            <table className="min-w-max w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 text-[#111827]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Date</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Company</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Contact Name</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Phone</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Email</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Message</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-6 px-4 text-[#4B5563]">No contact information available.</td>
                  </tr>
                ) : (
                  contacts.map((contact) => (
                    <tr key={contact.id} className="border-t border-gray-100 align-top">
                      <td className="py-3 px-4 text-xs text-[#4B5563] whitespace-nowrap">
                        {contact.created_at ? new Date(contact.created_at).toLocaleString() : "-"}
                      </td>
                      <td className="py-3 px-4 font-semibold text-[#111827]">{contact.company_name || "-"}</td>
                      <td className="py-3 px-4 text-[#111827]">{contact.contact_name || "-"}</td>
                      <td className="py-3 px-4 text-[#111827]">{contact.phone || "-"}</td>
                      <td className="py-3 px-4 text-[#4B5563]">{contact.email || "-"}</td>
                      <td className="py-3 px-4 text-[#4B5563] min-w-[220px] whitespace-pre-wrap">
                        {contact.message || "-"}
                      </td>
                      <td className="py-3 px-4">
                        <StatusSelect
                          value={normalizeSentStatus(contact.status)}
                          options={SENT_STATUS_OPTIONS}
                          onChange={(status) => updateContactStatus(contact.id, status)}
                          disabled={updatingContactStatusId === contact.id}
                          ariaLabel="Update contact status"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </AdminTableScroll>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-8" style={{ order: 40 }}>
          <div className="px-4 md:px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-[#111827]">Newsletter Subscribers</h2>
          </div>
          <AdminTableScroll>
            <table className="min-w-max w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 text-[#111827]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Date</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Email</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-6 px-4 text-[#4B5563]">No newsletter subscribers yet.</td>
                  </tr>
                ) : (
                  subscribers.map((subscriber) => (
                    <tr key={subscriber.id || subscriber.email} className="border-t border-gray-100">
                      <td className="py-3 px-4 text-xs text-[#4B5563] whitespace-nowrap">
                        {subscriber.created_at ? new Date(subscriber.created_at).toLocaleString() : "-"}
                      </td>
                      <td className="py-3 px-4 font-semibold text-[#111827]">{subscriber.email || "-"}</td>
                      <td className="py-3 px-4">
                        <StatusSelect
                          value={normalizeSubscriberStatus(subscriber.status)}
                          options={SUBSCRIBER_STATUS_OPTIONS}
                          onChange={(status) => updateSubscriberStatus(subscriber.id, status)}
                          disabled={updatingSubscriberStatusId === subscriber.id}
                          ariaLabel="Update subscriber status"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </AdminTableScroll>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-8" style={{ order: 10 }}>
          <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <h2 className="text-lg font-semibold text-[#111827]">Contractor Orders</h2>
            <input
              type="text"
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
              placeholder="Search by Order ID or Contractor Name"
              className="w-full md:w-96 border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <AdminTableScroll>
            <table className="min-w-max w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 text-[#111827]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Order</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Contractor</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Product</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Size</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Brand</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Quantity</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Vendor</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Order Status</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Payment Status</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Order Price</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">GST (18%)</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider">GST Input</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="py-6 px-4 text-[#4B5563]">No orders available.</td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => {
                    const orderItems = order.items || [];

                    return (
                      <tr key={order.id} className="border-t border-gray-100 align-top">
                        <td className="py-3 px-4">
                          <div className="font-semibold text-[#111827]">#{order.id.slice(0, 8)}</div>
                          <div className="text-xs text-[#4B5563]">{new Date(order.created_at).toLocaleString()}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-semibold text-[#111827]">{order.contractor_company}</div>
                          <div className="text-xs text-[#4B5563]">{order.contractor_name} · {order.contractor_phone || "-"}</div>
                        </td>
                        <td className="py-3 px-4">
                          <OrderItemsColumn items={orderItems} getValue={(item) => item?.product_name || "N/A"} className="min-w-[140px]" />
                        </td>
                        <td className="py-3 px-4">
                          <OrderItemsColumn items={orderItems} getValue={(item) => item?.size || "N/A"} className="min-w-[72px]" />
                        </td>
                        <td className="py-3 px-4">
                          <OrderItemsColumn items={orderItems} getValue={(item) => item?.brand || "N/A"} className="min-w-[90px]" />
                        </td>
                        <td className="py-3 px-4">
                          <OrderItemsColumn items={orderItems} getValue={formatOrderItemQuantity} className="min-w-[110px]" />
                        </td>
                        <td className="py-3 px-4">
                          <OrderItemsColumn items={orderItems} getValue={getOrderItemVendor} className="min-w-[120px]" />
                        </td>
                        <td className="py-3 px-4">
                          <div className="min-w-[150px] space-y-2">
                            <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${getOrderStatusClass(order.order_status)}`}>
                              {getOrderStatusLabel(order.order_status)}
                            </span>
                            <select
                              value={normalizeOrderStatus(order.order_status)}
                              onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                              disabled={updatingOrderStatusId === order.id}
                              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-[#111827] focus:border-[#C8A96A] focus:outline-none focus:ring-2 focus:ring-[#C8A96A]/20 disabled:opacity-60"
                              aria-label="Edit order status"
                            >
                              {ORDER_STATUS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${order.payment_status === "done" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                            {order.payment_status === "done" ? "Done" : "Not Done"}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          ₹{Number(order.total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                        </td>
                        <td className="py-3 px-4">
                          ₹{Number(order.gst_amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => updateGstStatus(order.id, !order.gst_input_done)}
                            disabled={updatingGstOrderId === order.id}
                            className={`rounded-sm px-3 py-1.5 text-xs font-semibold border ${
                              order.gst_input_done
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-white text-[#111827] border-gray-300"
                            } disabled:opacity-50`}
                          >
                            {order.gst_input_done ? "Done" : "Not Done"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </AdminTableScroll>
        </div>
      </section>
      <Footer />
    </div>
  );
}
